import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Play,
  Clock,
  BookOpen,
  ChevronDown,
  ChevronRight,
  Loader,
  Lock,
  Video,
  CheckCircle,
  Circle,
  Calendar,
  FileText,
} from 'lucide-react';
import { supabase } from '../lib/supabase';

const db = supabase as any;

interface MasterclassLesson {
  id: string;
  chapter_id: string;
  title: string;
  description: string | null;
  video_url: string | null;
  thumbnail_url: string | null;
  duration_seconds: number | null;
  sort_order: number;
  is_published: boolean;
  is_free: boolean;
}

interface MasterclassChapter {
  id: string;
  title: string;
  description: string | null;
  thumbnail_url: string | null;
  sort_order: number;
  is_published: boolean;
  lessons: MasterclassLesson[];
}

function getEmbedUrl(url: string): string | null {
  if (!url) return null;
  // YouTube
  const ytMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  // Loom
  const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (loomMatch) return `https://www.loom.com/embed/${loomMatch[1]}`;
  return null;
}

function formatDuration(seconds: number | null) {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export const MasterclassPublic: React.FC = () => {
  const { id: artistId } = useParams<{ id: string }>();
  const [chapters, setChapters] = useState<MasterclassChapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLesson, setSelectedLesson] = useState<MasterclassLesson | null>(null);
  const [expandedChapters, setExpandedChapters] = useState<Set<string>>(new Set());
  const [completedLessons, setCompletedLessons] = useState<Set<string>>(new Set());

  const fetchData = useCallback(async () => {
    if (!artistId) return;
    try {
      setLoading(true);
      const { data: chaptersData, error: chaptersError } = await db
        .from('masterclass_chapters')
        .select('*')
        .eq('is_published', true)
        .order('sort_order', { ascending: true });

      if (chaptersError) throw chaptersError;

      const { data: lessonsData, error: lessonsError } = await db
        .from('masterclass_lessons')
        .select('*')
        .eq('is_published', true)
        .order('sort_order', { ascending: true });

      if (lessonsError) throw lessonsError;

      // Fetch progress for this artist
      const { data: progressData } = await db
        .from('masterclass_progress')
        .select('lesson_id, completed')
        .eq('artist_id', artistId)
        .eq('completed', true);

      const completedSet = new Set<string>(
        (progressData || []).map((p: any) => p.lesson_id)
      );
      setCompletedLessons(completedSet);

      const chaptersWithLessons = (chaptersData || []).map((ch: any) => ({
        ...ch,
        lessons: (lessonsData || []).filter((l: any) => l.chapter_id === ch.id),
      }));

      setChapters(chaptersWithLessons);
      setExpandedChapters(new Set((chaptersData || []).map((ch: any) => ch.id)));

      // Auto-select first incomplete lesson with video, or first lesson
      const allLessons = chaptersWithLessons.flatMap((ch: MasterclassChapter) => ch.lessons);
      const firstIncomplete = allLessons.find(
        (l: MasterclassLesson) => l.video_url && !completedSet.has(l.id)
      );
      const firstWithVideo = allLessons.find((l: MasterclassLesson) => l.video_url);
      setSelectedLesson(firstIncomplete || firstWithVideo || null);
    } catch (err) {
      console.error('Error fetching masterclass:', err);
    } finally {
      setLoading(false);
    }
  }, [artistId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleComplete = async (lessonId: string) => {
    if (!artistId) return;
    const isCompleted = completedLessons.has(lessonId);

    // Optimistic update
    setCompletedLessons((prev) => {
      const next = new Set(prev);
      if (isCompleted) next.delete(lessonId);
      else next.add(lessonId);
      return next;
    });

    try {
      if (isCompleted) {
        // Mark as incomplete
        await db
          .from('masterclass_progress')
          .update({ completed: false, completed_at: null, updated_at: new Date().toISOString() })
          .eq('artist_id', artistId)
          .eq('lesson_id', lessonId);
      } else {
        // Mark as completed — upsert
        await db
          .from('masterclass_progress')
          .upsert(
            {
              artist_id: artistId,
              lesson_id: lessonId,
              completed: true,
              completed_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            },
            { onConflict: 'artist_id,lesson_id' }
          );
      }
    } catch (err) {
      console.error('Error updating progress:', err);
      // Revert optimistic update
      setCompletedLessons((prev) => {
        const next = new Set(prev);
        if (isCompleted) next.add(lessonId);
        else next.delete(lessonId);
        return next;
      });
    }
  };

  const toggleChapter = (id: string) => {
    setExpandedChapters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalLessons = chapters.reduce((sum, ch) => sum + ch.lessons.length, 0);
  const totalDuration = chapters.reduce(
    (sum, ch) =>
      sum + ch.lessons.reduce((s, l) => s + (l.duration_seconds || 0), 0),
    0
  );
  const completedCount = completedLessons.size;
  const progressPercent = totalLessons > 0 ? Math.round((completedCount / totalLessons) * 100) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader className="h-8 w-8 animate-spin text-[#0000fe]" />
      </div>
    );
  }

  const embedUrl = selectedLesson?.video_url
    ? getEmbedUrl(selectedLesson.video_url)
    : null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black text-white">
      {/* Artist Header */}
      <header className="bg-primary-500 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col space-y-4">
            <div className="flex items-center space-x-2">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" className="h-6 w-6" fill="currentColor">
                <path d="M96 160C96 124.7 124.7 96 160 96L480 96C515.3 96 544 124.7 544 160L544 480C544 515.3 515.3 544 480 544L160 544C124.7 544 96 515.3 96 480L96 160zM144 432L144 464C144 472.8 151.2 480 160 480L192 480C200.8 480 208 472.8 208 464L208 432C208 423.2 200.8 416 192 416L160 416C151.2 416 144 423.2 144 432zM448 416C439.2 416 432 423.2 432 432L432 464C432 472.8 439.2 480 448 480L480 480C488.8 480 496 472.8 496 464L496 432C496 423.2 488.8 416 480 416L448 416zM144 304L144 336C144 344.8 151.2 352 160 352L192 352C200.8 352 208 344.8 208 336L208 304C208 295.2 200.8 288 192 288L160 288C151.2 288 144 295.2 144 304zM448 288C439.2 288 432 295.2 432 304L432 336C432 344.8 439.2 352 448 352L480 352C488.8 352 496 344.8 496 336L496 304C496 295.2 488.8 288 480 288L448 288zM144 176L144 208C144 216.8 151.2 224 160 224L192 224C200.8 224 208 216.8 208 208L208 176C208 167.2 200.8 160 192 160L160 160C151.2 160 144 167.2 144 176zM448 160C439.2 160 432 167.2 432 176L432 208C432 216.8 439.2 224 448 224L480 224C488.8 224 496 216.8 496 208L496 176C496 167.2 488.8 160 480 160L448 160z"/>
              </svg>
              <span className="text-xl font-bold">VideoFeedback</span>
            </div>
            
            <div className="flex justify-between border-b border-white/20">
              <div className="flex space-x-4">
                <Link
                  to={`/artist/${artistId}`}
                  className="pb-2 px-1 text-white/70 hover:text-white"
                >
                  <div className="flex items-center space-x-2">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" className="h-6 w-6 sm:h-4 sm:w-4" fill="currentColor">
                      <path d="M96 160C96 124.7 124.7 96 160 96L480 96C515.3 96 544 124.7 544 160L544 480C544 515.3 515.3 544 480 544L160 544C124.7 544 96 515.3 96 480L96 160zM144 432L144 464C144 472.8 151.2 480 160 480L192 480C200.8 480 208 472.8 208 464L208 432C208 423.2 200.8 416 192 416L160 416C151.2 416 144 423.2 144 432zM448 416C439.2 416 432 423.2 432 432L432 464C432 472.8 439.2 480 448 480L480 480C488.8 480 496 472.8 496 464L496 432C496 423.2 488.8 416 480 416L448 416zM144 304L144 336C144 344.8 151.2 352 160 352L192 352C200.8 352 208 344.8 208 336L208 304C208 295.2 200.8 288 192 288L160 288C151.2 288 144 295.2 144 304zM448 288C439.2 288 432 295.2 432 304L432 336C432 344.8 439.2 352 448 352L480 352C488.8 352 496 344.8 496 336L496 304C496 295.2 488.8 288 480 288L448 288zM144 176L144 208C144 216.8 151.2 224 160 224L192 224C200.8 224 208 216.8 208 208L208 176C208 167.2 200.8 160 192 160L160 160C151.2 160 144 167.2 144 176zM448 160C439.2 160 432 167.2 432 176L432 208C432 216.8 439.2 224 448 224L480 224C488.8 224 496 216.8 496 208L496 176C496 167.2 488.8 160 480 160L448 160z"/>
                    </svg>
                    <span className="hidden sm:inline">Videos</span>
                  </div>
                </Link>
                <Link
                  to={`/artist/${artistId}/ad-creatives`}
                  className="pb-2 px-1 text-white/70 hover:text-white"
                >
                  <div className="flex items-center space-x-2">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" className="h-6 w-6 sm:h-4 sm:w-4" fill="currentColor">
                      <path d="M544 96C526.3 96 512 110.3 512 128L512 137.1L126.8 247.2C123 233.8 110.6 224 96 224C78.3 224 64 238.3 64 256L64 384C64 401.7 78.3 416 96 416C110.6 416 123 406.2 126.8 392.8L198.5 413.3C194.3 424.1 192 435.8 192 448C192 501 235 544 288 544C334.9 544 374 510.3 382.3 465.8L512 502.8L512 511.9C512 529.6 526.3 543.9 544 543.9C561.7 543.9 576 529.6 576 511.9L576 127.9C576 110.2 561.7 95.9 544 95.9zM335.8 452.5C333.5 476.9 313 496 288 496C261.5 496 240 474.5 240 448C240 440.3 241.8 433 245 426.6L335.8 452.5z"/>
                    </svg>
                    <span className="hidden sm:inline">Ad Creatives</span>
                  </div>
                </Link>
                <Link
                  to={`/artist/${artistId}/content-plan`}
                  className="pb-2 px-1 text-white/70 hover:text-white"
                >
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-6 w-6 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Content Plan</span>
                  </div>
                </Link>
              </div>
              
              <div className="flex space-x-4">
                <Link
                  to={`/artist/${artistId}/masterclass`}
                  className="pb-2 px-1 border-b-2 border-white text-white"
                >
                  <div className="flex items-center space-x-2">
                    <BookOpen className="h-6 w-6 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Learn</span>
                  </div>
                </Link>
                <Link
                  to={`/artist/${artistId}/release-sheets`}
                  className="pb-2 px-1 text-white/70 hover:text-white"
                >
                  <div className="flex items-center space-x-2">
                    <FileText className="h-6 w-6 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Sheets</span>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Masterclass subheader with progress */}
      <div className="border-b border-white/10 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 bg-[#0000fe] rounded-lg flex items-center justify-center">
                <BookOpen className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Masterclass</p>
                <p className="text-xs text-gray-400">
                  {chapters.length} chapter{chapters.length !== 1 ? 's' : ''}
                  {' · '}
                  {totalLessons} lesson{totalLessons !== 1 ? 's' : ''}
                  {totalDuration > 0 && (
                    <>
                      {' · '}
                      {Math.round(totalDuration / 60)} min total
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* Progress indicator */}
            {totalLessons > 0 && (
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-sm font-medium">{progressPercent}% complete</p>
                  <p className="text-xs text-gray-500">
                    {completedCount}/{totalLessons} lessons
                  </p>
                </div>
                <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#0000fe] rounded-full transition-all duration-500"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8 flex-col lg:flex-row">
          {/* Video Player */}
          <div className="flex-1 min-w-0">
            {selectedLesson ? (
              <div>
                {embedUrl ? (
                  <div className="aspect-video bg-black rounded-xl overflow-hidden">
                    <iframe
                      src={embedUrl}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={selectedLesson.title}
                    />
                  </div>
                ) : selectedLesson.video_url ? (
                  <div className="aspect-video bg-black rounded-xl overflow-hidden">
                    <video
                      src={selectedLesson.video_url}
                      controls
                      className="w-full h-full"
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-[#111] rounded-xl flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <Video className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No video available for this lesson</p>
                    </div>
                  </div>
                )}

                <div className="mt-6 flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-bold">{selectedLesson.title}</h2>
                    {selectedLesson.description && (
                      <p className="text-gray-400 mt-2 leading-relaxed">
                        {selectedLesson.description}
                      </p>
                    )}
                    {selectedLesson.duration_seconds && (
                      <div className="flex items-center gap-2 mt-3 text-sm text-gray-500">
                        <Clock className="h-4 w-4" />
                        {formatDuration(selectedLesson.duration_seconds)}
                      </div>
                    )}
                  </div>

                  {/* Mark complete button */}
                  <button
                    onClick={() => toggleComplete(selectedLesson.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium flex-shrink-0 transition-colors ${
                      completedLessons.has(selectedLesson.id)
                        ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                        : 'bg-white/10 text-gray-300 hover:bg-white/20'
                    }`}
                  >
                    {completedLessons.has(selectedLesson.id) ? (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        Completed
                      </>
                    ) : (
                      <>
                        <Circle className="h-4 w-4" />
                        Mark Complete
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="aspect-video bg-[#111] rounded-xl flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-lg font-medium">Welcome to the Masterclass</p>
                  <p className="text-sm mt-1">Select a lesson from the sidebar to begin</p>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar / Course Outline */}
          <div className="w-full lg:w-96 flex-shrink-0">
            <div className="bg-[#111] rounded-xl border border-white/5 overflow-hidden">
              <div className="p-4 border-b border-white/5">
                <h3 className="font-semibold text-lg">Course Content</h3>
              </div>

              <div className="divide-y divide-white/5">
                {chapters.map((chapter, chapterIndex) => {
                  const chapterCompleted = chapter.lessons.filter((l) =>
                    completedLessons.has(l.id)
                  ).length;
                  const chapterTotal = chapter.lessons.length;

                  return (
                    <div key={chapter.id}>
                      {/* Chapter Header */}
                      <button
                        onClick={() => toggleChapter(chapter.id)}
                        className="w-full flex items-center gap-3 p-4 hover:bg-white/5 transition-colors text-left"
                      >
                        {expandedChapters.has(chapter.id) ? (
                          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        )}

                        {chapter.thumbnail_url ? (
                          <img
                            src={chapter.thumbnail_url}
                            alt=""
                            className="h-8 w-12 object-cover rounded flex-shrink-0"
                          />
                        ) : (
                          <div className="h-8 w-12 bg-[#1a1a1a] rounded flex items-center justify-center flex-shrink-0">
                            <BookOpen className="h-3.5 w-3.5 text-gray-600" />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            <span className="text-gray-500 mr-1">
                              {chapterIndex + 1}.
                            </span>
                            {chapter.title}
                          </p>
                          <p className="text-xs text-gray-500">
                            {chapterCompleted}/{chapterTotal} completed
                          </p>
                        </div>

                        {/* Chapter progress ring */}
                        {chapterTotal > 0 && chapterCompleted === chapterTotal && (
                          <CheckCircle className="h-4 w-4 text-green-400 flex-shrink-0" />
                        )}
                      </button>

                      {/* Lessons */}
                      {expandedChapters.has(chapter.id) && (
                        <div className="bg-[#0d0d0d]">
                          {chapter.lessons.map((lesson, lessonIndex) => {
                            const isSelected = selectedLesson?.id === lesson.id;
                            const hasVideo = !!lesson.video_url;
                            const isCompleted = completedLessons.has(lesson.id);

                            return (
                              <button
                                key={lesson.id}
                                onClick={() => {
                                  if (hasVideo || lesson.is_free) {
                                    setSelectedLesson(lesson);
                                  }
                                }}
                                disabled={!hasVideo && !lesson.is_free}
                                className={`w-full flex items-center gap-3 px-4 py-3 pl-12 text-left transition-colors ${
                                  isSelected
                                    ? 'bg-[#0000fe]/10 border-l-2 border-[#0000fe]'
                                    : 'hover:bg-white/5 border-l-2 border-transparent'
                                } ${!hasVideo && !lesson.is_free ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                              >
                                {/* Status icon */}
                                <div className="flex-shrink-0">
                                  {isCompleted ? (
                                    <div className="h-7 w-7 rounded-full bg-green-500/20 flex items-center justify-center">
                                      <CheckCircle className="h-4 w-4 text-green-400" />
                                    </div>
                                  ) : hasVideo ? (
                                    <div
                                      className={`h-7 w-7 rounded-full flex items-center justify-center ${
                                        isSelected
                                          ? 'bg-[#0000fe] text-white'
                                          : 'bg-white/10 text-gray-400'
                                      }`}
                                    >
                                      <Play className="h-3 w-3 ml-0.5" />
                                    </div>
                                  ) : (
                                    <div className="h-7 w-7 rounded-full bg-white/5 flex items-center justify-center">
                                      <Lock className="h-3 w-3 text-gray-600" />
                                    </div>
                                  )}
                                </div>

                                {/* Thumbnail */}
                                {lesson.thumbnail_url && (
                                  <img
                                    src={lesson.thumbnail_url}
                                    alt=""
                                    className="h-9 w-16 object-cover rounded flex-shrink-0"
                                  />
                                )}

                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                  <p
                                    className={`text-sm truncate ${
                                      isCompleted
                                        ? 'text-gray-500 line-through'
                                        : isSelected
                                        ? 'text-white font-medium'
                                        : 'text-gray-300'
                                    }`}
                                  >
                                    <span className="text-gray-500 mr-1">
                                      {chapterIndex + 1}.{lessonIndex + 1}
                                    </span>
                                    {lesson.title}
                                  </p>
                                  {lesson.duration_seconds && (
                                    <p className="text-xs text-gray-500 mt-0.5">
                                      {formatDuration(lesson.duration_seconds)}
                                    </p>
                                  )}
                                </div>

                                {/* Free badge */}
                                {lesson.is_free && (
                                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-[#0000fe]/20 text-[#6666ff] flex-shrink-0">
                                    FREE
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {chapters.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p>No content available yet</p>
                  <p className="text-sm mt-1">Check back soon!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
